import React, { useState, useEffect } from 'react';
import {
  Paper,
  Title,
  Group,
  Button,
  Text,
  Stack,
  Card,
  Badge,
  Avatar,
  SimpleGrid,
  TextInput,
  Select,
  ActionIcon,
  Menu,
  rem,
  Grid,
  Tabs,
  Alert,
  Modal,
  Progress,
  ThemeIcon,
  Textarea,
  Timeline,
  RingProgress,
  SegmentedControl,
  MultiSelect,
  Slider,
  Table,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { AreaChart, BarChart, RadarChart } from '@mantine/charts';
import {
  IconSearch,
  IconEdit,
  IconChevronLeft,
  IconTrendingUp,
  IconAward,
  IconChartBar,
  IconTarget,
  IconBrain,
  IconHeart,
  IconPalette,
  IconRun,
  IconDownload,
  IconPlus,
  IconNotes,
  IconAlertCircle,
  IconCircleCheck,
  IconProgress,
  IconEye,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import {
  useTeacherClasses,
  useClassStudents,
  type TeacherStudent,
} from '../../hooks/useTeacher';
import { SmartLoader } from '../../components/common/LoadingStates';

// Progress tracking interfaces
interface SkillCategory {
  id: string;
  name: string;
  icon: React.ReactNode;
  color: string;
  skills: Skill[];
}

interface Skill {
  id: string;
  name: string;
  description: string;
  category: string;
  milestones: Milestone[];
}

interface Milestone {
  id: string;
  skill_id: string;
  name: string;
  description: string;
  order: number;
  achieved: boolean;
  achieved_date?: string;
  notes?: string;
}

interface Assessment {
  id: string;
  student_id: number;
  date: string;
  type: 'observation' | 'formal' | 'milestone';
  category: string;
  skill_id: string;
  score: number; // 1-5 scale
  notes: string;
  evidence?: string[];
}


const StudentProgressPage: React.FC = () => {
  const navigate = useNavigate();
  const [selectedClassId, setSelectedClassId] = useState<string>('');
  const [selectedStudent, setSelectedStudent] = useState<TeacherStudent | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [assessmentModalOpened, { open: openAssessmentModal, close: closeAssessmentModal }] = useDisclosure(false);
  const [progressModalOpened, { open: openProgressModal, close: closeProgressModal }] = useDisclosure(false);
  const [reportModalOpened, { open: openReportModal, close: closeReportModal }] = useDisclosure(false);

  // Form states
  const [assessmentForm, setAssessmentForm] = useState({
    category: '',
    skill_id: '',
    score: 3,
    notes: '',
    date: new Date(),
  });

  const {
    data: classes,
    isLoading: classesLoading,
  } = useTeacherClasses();

  const {
    data: students,
    isLoading: studentsLoading,
    error: studentsError,
  } = useClassStudents(selectedClassId);

  // Set initial class when classes are loaded
  useEffect(() => {
    if (classes && classes.length > 0 && !selectedClassId) {
      setSelectedClassId(classes[0].id);
    }
  }, [classes, selectedClassId]);

  // Mock skill categories and data
  const skillCategories: SkillCategory[] = [
    {
      id: 'cognitive',
      name: 'Cognitive Development',
      icon: <IconBrain size={20} />,
      color: 'blue',
      skills: [
        {
          id: 'counting',
          name: 'Counting & Numbers',
          description: 'Understanding numbers and counting objects',
          category: 'cognitive',
          milestones: [
            { id: 'm1', skill_id: 'counting', name: 'Counts to 10', description: 'Can count to 10 independently', order: 1, achieved: true, achieved_date: '2024-01-15' },
            { id: 'm2', skill_id: 'counting', name: 'Counts to 20', description: 'Can count to 20 independently', order: 2, achieved: true, achieved_date: '2024-02-20' },
            { id: 'm3', skill_id: 'counting', name: 'One-to-one correspondence', description: 'Counts objects with one-to-one correspondence', order: 3, achieved: false },
          ],
        },
        {
          id: 'letters',
          name: 'Letter Recognition',
          description: 'Recognizing and naming letters',
          category: 'cognitive',
          milestones: [
            { id: 'm4', skill_id: 'letters', name: 'Recognizes name', description: 'Recognizes own name in print', order: 1, achieved: true, achieved_date: '2024-01-10' },
            { id: 'm5', skill_id: 'letters', name: 'Knows 10 letters', description: 'Can identify at least 10 letters', order: 2, achieved: true, achieved_date: '2024-02-15' },
            { id: 'm6', skill_id: 'letters', name: 'Knows all letters', description: 'Can identify all 26 letters', order: 3, achieved: false },
          ],
        },
      ],
    },
    {
      id: 'social',
      name: 'Social & Emotional',
      icon: <IconHeart size={20} />,
      color: 'pink',
      skills: [
        {
          id: 'sharing',
          name: 'Sharing & Cooperation',
          description: 'Ability to share and work with others',
          category: 'social',
          milestones: [
            { id: 'm7', skill_id: 'sharing', name: 'Takes turns', description: 'Takes turns during activities', order: 1, achieved: true, achieved_date: '2024-01-20' },
            { id: 'm8', skill_id: 'sharing', name: 'Shares materials', description: 'Shares materials without prompting', order: 2, achieved: false },
          ],
        },
      ],
    },
    {
      id: 'physical',
      name: 'Physical Development',
      icon: <IconRun size={20} />,
      color: 'green',
      skills: [
        {
          id: 'fine_motor',
          name: 'Fine Motor Skills',
          description: 'Small muscle control and coordination',
          category: 'physical',
          milestones: [
            { id: 'm9', skill_id: 'fine_motor', name: 'Holds pencil correctly', description: 'Uses proper pencil grip', order: 1, achieved: true, achieved_date: '2024-01-25' },
            { id: 'm10', skill_id: 'fine_motor', name: 'Cuts with scissors', description: 'Can cut along a line with scissors', order: 2, achieved: false },
          ],
        },
      ],
    },
    {
      id: 'creative',
      name: 'Creative Expression',
      icon: <IconPalette size={20} />,
      color: 'violet',
      skills: [
        {
          id: 'art',
          name: 'Art & Drawing',
          description: 'Creative expression through art',
          category: 'creative',
          milestones: [
            { id: 'm11', skill_id: 'art', name: 'Draws basic shapes', description: 'Can draw circles, squares, triangles', order: 1, achieved: true, achieved_date: '2024-02-01' },
            { id: 'm12', skill_id: 'art', name: 'Draws people', description: 'Draws recognizable people with details', order: 2, achieved: false },
          ],
        },
      ],
    },
  ];

  // Mock assessment data
  const mockAssessments: Assessment[] = [
    {
      id: '1',
      student_id: 1,
      date: '2024-03-15',
      type: 'observation',
      category: 'cognitive',
      skill_id: 'counting',
      score: 4,
      notes: 'Counted to 20 independently during math center time',
    },
    {
      id: '2',
      student_id: 1,
      date: '2024-03-10',
      type: 'milestone',
      category: 'social',
      skill_id: 'sharing',
      score: 3,
      notes: 'Shared blocks with peers during free play',
    },
  ];

  // Mock progress data for charts
  const progressData = [
    { month: 'Jan', cognitive: 3.2, social: 3.5, physical: 3.8, creative: 3.0 },
    { month: 'Feb', cognitive: 3.5, social: 3.7, physical: 4.0, creative: 3.3 },
    { month: 'Mar', cognitive: 3.8, social: 3.9, physical: 4.2, creative: 3.6 },
  ];

  const radarData = [
    { skill: 'Numbers', score: 4 },
    { skill: 'Letters', score: 3.5 },
    { skill: 'Social', score: 4.2 },
    { skill: 'Motor', score: 3.8 },
    { skill: 'Creative', score: 3.3 },
    { skill: 'Language', score: 3.7 },
  ];

  const filteredStudents = students?.filter(student => {
    const matchesSearch =
      student.first_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      student.last_name.toLowerCase().includes(searchQuery.toLowerCase());

    return matchesSearch;
  }) || [];

  const getProgressColor = (score: number) => {
    if (score >= 4.5) return 'green';
    if (score >= 3.5) return 'blue';
    if (score >= 2.5) return 'yellow';
    return 'red';
  };

  const getProgressLabel = (score: number) => {
    if (score >= 4.5) return 'Exceeding';
    if (score >= 3.5) return 'Meeting';
    if (score >= 2.5) return 'Approaching';
    return 'Developing';
  };

  const calculateOverallProgress = (_studentId: number) => {
    // Mock calculation
    const scores = mockAssessments
      .filter(a => a.student_id === _studentId)
      .map(a => a.score);

    if (scores.length === 0) return 0;
    return scores.reduce((a, b) => a + b, 0) / scores.length;
  };

  const getMilestoneStats = (_studentId: number) => {
    // Mock calculation
    let achieved = 0;
    let total = 0;

    skillCategories.forEach(category => {
      category.skills.forEach(skill => {
        skill.milestones.forEach(milestone => {
          total++;
          if (milestone.achieved) achieved++;
        });
      });
    });

    return { achieved, total, percentage: total > 0 ? (achieved / total) * 100 : 0 };
  };

  const handleStudentClick = (student: TeacherStudent) => {
    setSelectedStudent(student);
    openProgressModal();
  };

  const handleAddAssessment = (student: TeacherStudent) => {
    setSelectedStudent(student);
    setAssessmentForm({
      category: '',
      skill_id: '',
      score: 3,
      notes: '',
      date: new Date(),
    });
    openAssessmentModal();
  };

  const handleSaveAssessment = () => {
    notifications.show({
      title: 'Assessment Saved',
      message: `Assessment for ${selectedStudent?.first_name} has been recorded`,
      color: 'green',
    });
    closeAssessmentModal();
  };

  const handleGenerateReport = (student: TeacherStudent) => {
    setSelectedStudent(student);
    openReportModal();
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Card withBorder>
        <Group justify="space-between">
          <Group>
            <ActionIcon
              variant="subtle"
              size="lg"
              onClick={() => navigate('/teacher/dashboard')}
            >
              <IconChevronLeft size={20} />
            </ActionIcon>
            <div>
              <Title order={2}>Student Progress</Title>
              <Text size="sm" c="dimmed">
                Track development and milestones
              </Text>
            </div>
          </Group>
          <Group>
            <SegmentedControl
              value={viewMode}
              onChange={(value) => setViewMode(value as 'grid' | 'list')}
              data={[
                { label: 'Grid', value: 'grid' },
                { label: 'List', value: 'list' },
              ]}
            />
            <Button
              variant="light"
              leftSection={<IconDownload size={16} />}
              disabled
            >
              Export Reports
            </Button>
          </Group>
        </Group>
      </Card>

      {/* Class Overview Stats */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Class Average
              </Text>
              <Text fw={700} size="xl">3.7</Text>
              <Text size="xs" c="dimmed">Meeting Expectations</Text>
            </div>
            <RingProgress
              size={60}
              thickness={6}
              sections={[{ value: 74, color: 'blue' }]}
              label={
                <Text size="xs" ta="center" fw={700}>
                  74%
                </Text>
              }
            />
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Milestones
              </Text>
              <Text fw={700} size="xl">142/180</Text>
              <Text size="xs" c="dimmed">Achieved</Text>
            </div>
            <ThemeIcon color="green" variant="light" size="xl">
              <IconAward size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Assessments
              </Text>
              <Text fw={700} size="xl">24</Text>
              <Text size="xs" c="dimmed">This Month</Text>
            </div>
            <ThemeIcon color="violet" variant="light" size="xl">
              <IconChartBar size={24} />
            </ThemeIcon>
          </Group>
        </Card>
        <Card withBorder>
          <Group justify="space-between">
            <div>
              <Text size="xs" tt="uppercase" fw={700} c="dimmed">
                Need Support
              </Text>
              <Text fw={700} size="xl">3</Text>
              <Text size="xs" c="dimmed">Students</Text>
            </div>
            <ThemeIcon color="orange" variant="light" size="xl">
              <IconAlertCircle size={24} />
            </ThemeIcon>
          </Group>
        </Card>
      </SimpleGrid>

      {/* Class Selector */}
      <SmartLoader isLoading={classesLoading}>
        {classes && classes.length > 1 && (
          <Card withBorder>
            <Title order={4} mb="md">Select Class</Title>
            <SegmentedControl
              value={selectedClassId.toString()}
              onChange={(value) => setSelectedClassId(value || '')}
              data={classes.filter(cls => cls && cls.name).map(cls => ({
                label: `${cls.name} (${cls.student_count || 0})`,
                value: cls.id.toString(),
              }))}
              fullWidth
            />
          </Card>
        )}
      </SmartLoader>

      {/* Category Filter */}
      <Card withBorder>
        <Title order={4} mb="md">Skill Categories</Title>
        <Group>
          <Button
            variant={selectedCategory === 'all' ? 'filled' : 'light'}
            size="sm"
            onClick={() => setSelectedCategory('all')}
          >
            All Categories
          </Button>
          {skillCategories.map(category => (
            <Button
              key={category.id}
              variant={selectedCategory === category.id ? 'filled' : 'light'}
              color={category.color}
              size="sm"
              leftSection={category.icon}
              onClick={() => setSelectedCategory(category.id)}
            >
              {category.name}
            </Button>
          ))}
        </Group>
      </Card>

      {/* Filters */}
      <Card withBorder>
        <Grid>
          <Grid.Col span={{ base: 12, sm: 6 }}>
            <TextInput
              placeholder="Search students..."
              leftSection={<IconSearch size={16} />}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </Grid.Col>
        </Grid>
      </Card>

      {/* Students Progress Grid */}
      <SmartLoader
        isLoading={studentsLoading}
        error={studentsError as Error | null}
        isEmpty={!filteredStudents.length}
        emptyState={
          <Alert color="blue" title="No students found">
            {searchQuery ? "No students match your search." : "No students in this class."}
          </Alert>
        }
      >
        {viewMode === 'grid' ? (
          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {filteredStudents.map((student: any) => {
              const overallScore = calculateOverallProgress(student.id);
              const milestoneStats = getMilestoneStats(student.id);

              return (
                <Card
                  key={student.id}
                  withBorder
                  radius="md"
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleStudentClick(student)}
                >
                  <Group justify="space-between" mb="md">
                    <Group>
                      <Avatar size="lg" radius="xl">
                        {(student.first_name || '')[0]}{(student.last_name || '')[0]}
                      </Avatar>
                      <div>
                        <Text fw={500}>{student.first_name || ''} {student.last_name || ''}</Text>
                        <Text size="sm" c="dimmed">Age {student.age}</Text>
                      </div>
                    </Group>
                    <Menu shadow="md" width={200}>
                      <Menu.Target>
                        <ActionIcon variant="subtle" onClick={(e) => e.stopPropagation()}>
                          <IconEdit size={18} />
                        </ActionIcon>
                      </Menu.Target>
                      <Menu.Dropdown>
                        <Menu.Item
                          leftSection={<IconPlus style={{ width: rem(14), height: rem(14) }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleAddAssessment(student);
                          }}
                        >
                          Add Assessment
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconChartBar style={{ width: rem(14), height: rem(14) }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleStudentClick(student);
                          }}
                        >
                          View Progress
                        </Menu.Item>
                        <Menu.Item
                          leftSection={<IconNotes style={{ width: rem(14), height: rem(14) }} />}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleGenerateReport(student);
                          }}
                        >
                          Generate Report
                        </Menu.Item>
                      </Menu.Dropdown>
                    </Menu>
                  </Group>

                  <Stack gap="sm">
                    {/* Overall Progress */}
                    <div>
                      <Group justify="space-between" mb={4}>
                        <Text size="sm" fw={500}>Overall Progress</Text>
                        <Badge color={getProgressColor(overallScore)} size="sm">
                          {getProgressLabel(overallScore)}
                        </Badge>
                      </Group>
                      <Progress
                        value={overallScore * 20}
                        color={getProgressColor(overallScore)}
                        size="lg"
                        radius="xl"
                      />
                    </div>

                    {/* Milestones */}
                    <Group justify="space-between">
                      <Text size="sm" c="dimmed">Milestones</Text>
                      <Group gap={4}>
                        <Text size="sm" fw={500}>
                          {milestoneStats.achieved}/{milestoneStats.total}
                        </Text>
                        <Text size="xs" c="dimmed">
                          ({Math.round(milestoneStats.percentage)}%)
                        </Text>
                      </Group>
                    </Group>

                    {/* Category Progress */}
                    <SimpleGrid cols={2} spacing={4}>
                      {skillCategories.map(category => (
                        <Group key={category.id} gap={4}>
                          <ThemeIcon color={category.color} variant="light" size="xs">
                            {category.icon}
                          </ThemeIcon>
                          <Progress
                            value={75}
                            color={category.color}
                            size="xs"
                            style={{ flex: 1 }}
                          />
                        </Group>
                      ))}
                    </SimpleGrid>

                    {/* Recent Assessment */}
                    <Card withBorder p="xs" bg="gray.0">
                      <Group justify="space-between">
                        <Text size="xs" c="dimmed">Last Assessment</Text>
                        <Text size="xs">Mar 15, 2024</Text>
                      </Group>
                    </Card>
                  </Stack>
                </Card>
              );
            })}
          </SimpleGrid>
        ) : (
          <Paper withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Student</Table.Th>
                  <Table.Th>Overall Progress</Table.Th>
                  <Table.Th>Cognitive</Table.Th>
                  <Table.Th>Social</Table.Th>
                  <Table.Th>Physical</Table.Th>
                  <Table.Th>Creative</Table.Th>
                  <Table.Th>Milestones</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {filteredStudents.map((student: any) => {
                  const overallScore = calculateOverallProgress(student.id);
                  const milestoneStats = getMilestoneStats(student.id);

                  return (
                    <Table.Tr key={student.id}>
                      <Table.Td>
                        <Group>
                          <Avatar size="sm" radius="xl">
                            {(student.first_name || '')[0]}{(student.last_name || '')[0]}
                          </Avatar>
                          <div>
                            <Text size="sm" fw={500}>
                              {student.first_name || ''} {student.last_name || ''}
                            </Text>
                            <Text size="xs" c="dimmed">Age {student.age}</Text>
                          </div>
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getProgressColor(overallScore)}>
                          {getProgressLabel(overallScore)}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Progress value={80} color="blue" size="sm" />
                      </Table.Td>
                      <Table.Td>
                        <Progress value={75} color="pink" size="sm" />
                      </Table.Td>
                      <Table.Td>
                        <Progress value={85} color="green" size="sm" />
                      </Table.Td>
                      <Table.Td>
                        <Progress value={70} color="violet" size="sm" />
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">
                          {milestoneStats.achieved}/{milestoneStats.total}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="light"
                            onClick={() => handleStudentClick(student)}
                          >
                            <IconChartBar size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="light"
                            onClick={() => handleAddAssessment(student)}
                          >
                            <IconPlus size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        )}
      </SmartLoader>

      {/* Assessment Modal */}
      <Modal
        opened={assessmentModalOpened}
        onClose={closeAssessmentModal}
        title={`New Assessment - ${selectedStudent?.first_name} ${selectedStudent?.last_name}`}
        size="md"
      >
        <Stack gap="md">
          <DatePickerInput
            label="Assessment Date"
            value={assessmentForm.date}
            onChange={(date) => setAssessmentForm(prev => ({ ...prev, date: date || new Date() }))}
            maxDate={new Date()}
          />

          <Select
            label="Category"
            placeholder="Select category"
            data={skillCategories.filter(cat => cat && cat.name).map(cat => ({
              value: cat.id,
              label: cat.name,
            }))}
            value={assessmentForm.category}
            onChange={(value) => setAssessmentForm(prev => ({ ...prev, category: value || '' }))}
            required
          />

          {assessmentForm.category && (
            <Select
              label="Skill"
              placeholder="Select skill"
              data={
                skillCategories
                  .find(cat => cat.id === assessmentForm.category)
                  ?.skills.filter(skill => skill && skill.name).map(skill => ({
                    value: skill.id,
                    label: skill.name,
                  })) || []
              }
              value={assessmentForm.skill_id}
              onChange={(value) => setAssessmentForm(prev => ({ ...prev, skill_id: value || '' }))}
              required
            />
          )}

          <div>
            <Text size="sm" fw={500} mb="xs">Score</Text>
            <Slider
              value={assessmentForm.score}
              onChange={(value) => setAssessmentForm(prev => ({ ...prev, score: value }))}
              min={1}
              max={5}
              step={0.5}
              marks={[
                { value: 1, label: '1' },
                { value: 2, label: '2' },
                { value: 3, label: '3' },
                { value: 4, label: '4' },
                { value: 5, label: '5' },
              ]}
              label={(value) => {
                if (value >= 4.5) return 'Exceeding';
                if (value >= 3.5) return 'Meeting';
                if (value >= 2.5) return 'Approaching';
                return 'Developing';
              }}
            />
            <Group justify="space-between" mt="xs">
              <Text size="xs" c="dimmed">Developing</Text>
              <Text size="xs" c="dimmed">Exceeding</Text>
            </Group>
          </div>

          <Textarea
            label="Observation Notes"
            placeholder="Describe what you observed..."
            value={assessmentForm.notes}
            onChange={(e) => setAssessmentForm(prev => ({ ...prev, notes: e.target.value }))}
            rows={4}
            required
          />

          <Group justify="space-between">
            <Button variant="light" onClick={closeAssessmentModal}>
              Cancel
            </Button>
            <Button onClick={handleSaveAssessment}>
              Save Assessment
            </Button>
          </Group>
        </Stack>
      </Modal>

      {/* Progress Detail Modal */}
      <Modal
        opened={progressModalOpened}
        onClose={closeProgressModal}
        title={`Progress Details - ${selectedStudent?.first_name} ${selectedStudent?.last_name}`}
        size="xl"
        fullScreen
      >
        {selectedStudent && (
          <Stack gap="md">
            <Group justify="space-between">
              <Group>
                <Avatar size="xl" radius="xl">
                  {(selectedStudent.first_name || '')[0]}{(selectedStudent.last_name || '')[0]}
                </Avatar>
                <div>
                  <Title order={3}>{selectedStudent.first_name || ''} {selectedStudent.last_name || ''}</Title>
                  <Text c="dimmed">Age {selectedStudent.age}</Text>
                </div>
              </Group>
              <Group>
                <Button
                  variant="light"
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    closeProgressModal();
                    handleAddAssessment(selectedStudent);
                  }}
                >
                  Add Assessment
                </Button>
                <Button
                  variant="light"
                  leftSection={<IconNotes size={16} />}
                  onClick={() => {
                    closeProgressModal();
                    handleGenerateReport(selectedStudent);
                  }}
                >
                  Generate Report
                </Button>
              </Group>
            </Group>

            <Tabs defaultValue="overview">
              <Tabs.List>
                <Tabs.Tab value="overview" leftSection={<IconChartBar size={16} />}>
                  Overview
                </Tabs.Tab>
                <Tabs.Tab value="milestones" leftSection={<IconAward size={16} />}>
                  Milestones
                </Tabs.Tab>
                <Tabs.Tab value="assessments" leftSection={<IconNotes size={16} />}>
                  Assessments
                </Tabs.Tab>
                <Tabs.Tab value="trends" leftSection={<IconTrendingUp size={16} />}>
                  Trends
                </Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="overview" pt="md">
                <Grid>
                  <Grid.Col span={8}>
                    <Card withBorder>
                      <Title order={5} mb="md">Progress by Category</Title>
                      <AreaChart
                        h={300}
                        data={progressData}
                        dataKey="month"
                        series={[
                          { name: 'cognitive', color: 'blue.6' },
                          { name: 'social', color: 'pink.6' },
                          { name: 'physical', color: 'green.6' },
                          { name: 'creative', color: 'violet.6' },
                        ]}
                        curveType="linear"
                      />
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={4}>
                    <Card withBorder>
                      <Title order={5} mb="md">Skill Radar</Title>
                      <RadarChart
                        h={300}
                        data={radarData}
                        dataKey="skill"
                        series={[{ name: 'score', color: 'blue.6' }]}
                      />
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={12}>
                    <SimpleGrid cols={4} spacing="md">
                      {skillCategories.map(category => (
                        <Card key={category.id} withBorder>
                          <Group justify="space-between" mb="xs">
                            <ThemeIcon color={category.color} variant="light">
                              {category.icon}
                            </ThemeIcon>
                            <Badge color={category.color} size="sm">3.8</Badge>
                          </Group>
                          <Text size="sm" fw={500}>{category.name}</Text>
                          <Progress value={76} color={category.color} size="sm" mt="xs" />
                        </Card>
                      ))}
                    </SimpleGrid>
                  </Grid.Col>
                </Grid>
              </Tabs.Panel>

              <Tabs.Panel value="milestones" pt="md">
                <Stack gap="md">
                  {skillCategories.map(category => (
                    <Card key={category.id} withBorder>
                      <Group mb="md">
                        <ThemeIcon color={category.color} variant="light">
                          {category.icon}
                        </ThemeIcon>
                        <Title order={5}>{category.name}</Title>
                      </Group>

                      {category.skills.map(skill => (
                        <div key={skill.id} style={{ marginBottom: '1rem' }}>
                          <Text fw={500} mb="xs">{skill.name}</Text>
                          <Text size="sm" c="dimmed" mb="sm">{skill.description}</Text>

                          <Timeline active={skill.milestones.filter(m => m.achieved).length} bulletSize={24} lineWidth={2}>
                            {skill.milestones.map((milestone) => (
                              <Timeline.Item
                                key={milestone.id}
                                bullet={
                                  milestone.achieved ? (
                                    <IconCircleCheck size={12} />
                                  ) : (
                                    <IconProgress size={12} />
                                  )
                                }
                                color={milestone.achieved ? 'green' : 'gray'}
                                title={milestone.name}
                              >
                                <Text c="dimmed" size="sm">{milestone.description}</Text>
                                {milestone.achieved && (
                                  <Text size="xs" c="dimmed" mt={4}>
                                    Achieved on {new Date(milestone.achieved_date!).toLocaleDateString()}
                                  </Text>
                                )}
                              </Timeline.Item>
                            ))}
                          </Timeline>
                        </div>
                      ))}
                    </Card>
                  ))}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="assessments" pt="md">
                <Stack gap="md">
                  {mockAssessments.map(assessment => (
                    <Card key={assessment.id} withBorder>
                      <Group justify="space-between">
                        <div>
                          <Group gap="xs">
                            <Badge color={skillCategories.find(c => c.id === assessment.category)?.color}>
                              {assessment.category}
                            </Badge>
                            <Text size="sm" fw={500}>
                              {skillCategories
                                .find(c => c.id === assessment.category)
                                ?.skills.find(s => s.id === assessment.skill_id)?.name}
                            </Text>
                          </Group>
                          <Text size="sm" c="dimmed" mt="xs">
                            {assessment.notes}
                          </Text>
                        </div>
                        <div style={{ textAlign: 'right' }}>
                          <Badge
                            color={getProgressColor(assessment.score)}
                            size="lg"
                            variant="light"
                          >
                            {assessment.score}/5
                          </Badge>
                          <Text size="xs" c="dimmed" mt="xs">
                            {new Date(assessment.date).toLocaleDateString()}
                          </Text>
                        </div>
                      </Group>
                    </Card>
                  ))}
                </Stack>
              </Tabs.Panel>

              <Tabs.Panel value="trends" pt="md">
                <Grid>
                  <Grid.Col span={12}>
                    <Card withBorder>
                      <Title order={5} mb="md">Monthly Progress Trends</Title>
                      <BarChart
                        h={300}
                        data={progressData}
                        dataKey="month"
                        series={[
                          { name: 'cognitive', color: 'blue.6' },
                          { name: 'social', color: 'pink.6' },
                          { name: 'physical', color: 'green.6' },
                          { name: 'creative', color: 'violet.6' },
                        ]}
                      />
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Card withBorder>
                      <Title order={5} mb="md">Strengths</Title>
                      <Stack gap="sm">
                        <Group>
                          <ThemeIcon color="green" variant="light">
                            <IconTrendingUp size={20} />
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={500}>Fine Motor Skills</Text>
                            <Text size="xs" c="dimmed">Consistently exceeding expectations</Text>
                          </div>
                        </Group>
                        <Group>
                          <ThemeIcon color="green" variant="light">
                            <IconTrendingUp size={20} />
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={500}>Social Interaction</Text>
                            <Text size="xs" c="dimmed">Shows leadership qualities</Text>
                          </div>
                        </Group>
                      </Stack>
                    </Card>
                  </Grid.Col>
                  <Grid.Col span={6}>
                    <Card withBorder>
                      <Title order={5} mb="md">Areas for Growth</Title>
                      <Stack gap="sm">
                        <Group>
                          <ThemeIcon color="orange" variant="light">
                            <IconTarget size={20} />
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={500}>Letter Recognition</Text>
                            <Text size="xs" c="dimmed">Needs additional practice</Text>
                          </div>
                        </Group>
                        <Group>
                          <ThemeIcon color="orange" variant="light">
                            <IconTarget size={20} />
                          </ThemeIcon>
                          <div style={{ flex: 1 }}>
                            <Text size="sm" fw={500}>Scissor Skills</Text>
                            <Text size="xs" c="dimmed">Developing fine motor control</Text>
                          </div>
                        </Group>
                      </Stack>
                    </Card>
                  </Grid.Col>
                </Grid>
              </Tabs.Panel>
            </Tabs>
          </Stack>
        )}
      </Modal>

      {/* Report Generation Modal */}
      <Modal
        opened={reportModalOpened}
        onClose={closeReportModal}
        title="Generate Progress Report"
        size="lg"
      >
        {selectedStudent && (
          <Stack gap="md">
            <Alert color="blue" icon={<IconNotes size={16} />}>
              Generate a comprehensive progress report for {selectedStudent.first_name} {selectedStudent.last_name}
            </Alert>

            <Select
              label="Report Period"
              placeholder="Select period"
              data={[
                { value: 'monthly', label: 'Monthly Report' },
                { value: 'quarterly', label: 'Quarterly Report' },
                { value: 'semester', label: 'Semester Report' },
                { value: 'annual', label: 'Annual Report' },
              ]}
              defaultValue="quarterly"
            />

            <MultiSelect
              label="Include Sections"
              placeholder="Select sections to include"
              data={[
                { value: 'overview', label: 'Progress Overview' },
                { value: 'milestones', label: 'Milestone Achievements' },
                { value: 'assessments', label: 'Assessment Details' },
                { value: 'strengths', label: 'Strengths & Growth Areas' },
                { value: 'goals', label: 'Goals & Recommendations' },
                { value: 'photos', label: 'Activity Photos' },
              ]}
              defaultValue={['overview', 'milestones', 'strengths', 'goals']}
            />

            <Textarea
              label="Additional Comments"
              placeholder="Add any specific comments for this report..."
              rows={4}
            />

            <Group justify="space-between">
              <Button variant="light" onClick={closeReportModal}>
                Cancel
              </Button>
              <Group>
                <Button variant="light" leftSection={<IconEye size={16} />}>
                  Preview
                </Button>
                <Button leftSection={<IconDownload size={16} />}>
                  Generate Report
                </Button>
              </Group>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
};

export default StudentProgressPage;
